import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  Alert,
  Divider,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { TrendingUp, History, Analytics, People } from '@mui/icons-material';
import './ForecastModals.css';

const DailyForecastUpdate = ({ open, onClose, onSave }) => {
  const [tomorrowsDate, setTomorrowsDate] = useState('');
  const [currentForecasts, setCurrentForecasts] = useState([]);
  const [revisedForecasts, setRevisedForecasts] = useState({});
  const [revisedAppointments, setRevisedAppointments] = useState({});
  const [revisionReason, setRevisionReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [forecastHistory, setForecastHistory] = useState([]);
  const [totalChanges, setTotalChanges] = useState({ revenue: 0, appointments: 0 });

  const revisionReasons = [
    'New bookings added',
    'Cancellations received',
    'Staff availability changed',
    'Client rescheduling',
    'Seasonal adjustment',
    'Market conditions',
    'Other'
  ];

  useEffect(() => {
    if (open) {
      loadTomorrowsForecasts();
    }
  }, [open]);

  const loadTomorrowsForecasts = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Get tomorrow's date
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const tomorrowStr = tomorrow.toISOString().split('T')[0];
      setTomorrowsDate(tomorrowStr);
      
      // Get current forecasts for tomorrow
      const response = await fetch(`/api/forecast/daily-forecast?date=${tomorrowStr}`);
      const data = await response.json();
      
      if (data.success) {
        setCurrentForecasts(data.forecasts);
        
        // Initialize revised forecasts with current values
        const initialRevised = {};
        const initialAppointments = {};
        
        data.forecasts.forEach(forecast => {
          initialRevised[forecast.staff_member_id] = forecast.latest_forecast || forecast.original_forecast;
          initialAppointments[forecast.staff_member_id] = forecast.latest_appointments || forecast.original_appointments || 0;
        });
        
        setRevisedForecasts(initialRevised);
        setRevisedAppointments(initialAppointments);
        
        // Load revision history
        loadRevisionHistory(tomorrowStr);
      } else {
        setError(data.message || 'Failed to load forecasts');
      }
    } catch (err) {
      setError('Error loading tomorrow\'s forecasts');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadRevisionHistory = async (date) => {
    try {
      const response = await fetch(`/api/forecast/revision-history?date=${date}`);
      const data = await response.json();
      
      if (data.success) {
        setForecastHistory(data.revisions);
      }
    } catch (err) {
      console.error('Error loading revision history:', err);
    }
  };

  const handleForecastChange = (staffMemberId, value) => {
    const numericValue = parseFloat(value) || 0;
    setRevisedForecasts(prev => ({
      ...prev,
      [staffMemberId]: numericValue
    }));
  };

  const handleAppointmentChange = (staffMemberId, value) => {
    const numericValue = parseInt(value) || 0;
    setRevisedAppointments(prev => ({
      ...prev,
      [staffMemberId]: numericValue
    }));
  };

  // Auto-calculate total changes whenever revised values change
  useEffect(() => {
    if (currentForecasts.length > 0) {
      let revenueChange = 0;
      let appointmentChange = 0;

      currentForecasts.forEach(forecast => {
        const staffId = forecast.staff_member_id;
        
        // Revenue changes
        const originalRevenue = forecast.latest_forecast || forecast.original_forecast || 0;
        const revisedRevenue = revisedForecasts[staffId] || originalRevenue;
        revenueChange += (revisedRevenue - originalRevenue);
        
        // Appointment changes
        const originalAppts = forecast.latest_appointments || forecast.original_appointments || 0;
        const revisedAppts = revisedAppointments[staffId] || originalAppts;
        appointmentChange += (revisedAppts - originalAppts);
      });

      setTotalChanges({ revenue: revenueChange, appointments: appointmentChange });
    }
  }, [revisedForecasts, revisedAppointments, currentForecasts]);

  const handleSave = async () => {
    if (!revisionReason.trim()) {
      setError('Please provide a reason for the forecast revision');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const revisions = Object.entries(revisedForecasts).map(([staffMemberId, revisedAmount]) => {
        const original = currentForecasts.find(f => f.staff_member_id === parseInt(staffMemberId));
        const revisedAppts = revisedAppointments[staffMemberId] || 0;
        
        return {
          staff_member_id: parseInt(staffMemberId),
          forecast_date: tomorrowsDate,
          original_forecast: original?.original_forecast || 0,
          latest_forecast: original?.latest_forecast || original?.original_forecast || 0,
          revised_forecast: revisedAmount,
          original_appointments: original?.original_appointments || 0,
          latest_appointments: original?.latest_appointments || original?.original_appointments || 0,
          revised_appointments: revisedAppts,
          revision_reason: revisionReason,
          revision_date: new Date().toISOString()
        };
      });

      const response = await fetch('/api/forecast/daily-update', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          date: tomorrowsDate,
          revisions: revisions,
          revision_reason: revisionReason
        }),
      });

      const data = await response.json();

      if (data.success) {
        onSave && onSave(data);
        onClose();
      } else {
        setError(data.message || 'Failed to save forecast revisions');
      }
    } catch (err) {
      setError('Error saving forecast revisions');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getTotalCurrent = () => {
    return currentForecasts.reduce((sum, forecast) => 
      sum + (forecast.latest_forecast || forecast.original_forecast), 0
    );
  };

  const getTotalRevised = () => {
    return Object.values(revisedForecasts).reduce((sum, value) => sum + value, 0);
  };

  const getTotalCurrentAppointments = () => {
    return currentForecasts.reduce((sum, forecast) => 
      sum + (forecast.latest_appointments || forecast.original_appointments || 0), 0
    );
  };

  const getTotalRevisedAppointments = () => {
    return Object.values(revisedAppointments).reduce((sum, value) => sum + value, 0);
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      className="forecast-modal-overlay"
    >
      <DialogTitle className="forecast-modal-title">
        <Box display="flex" alignItems="center" gap={2}>
          <TrendingUp />
          <div>
            <Typography variant="h6">Daily Forecast Update</Typography>
            <Typography variant="body2" color="textSecondary">
              {tomorrowsDate && formatDate(tomorrowsDate)}
            </Typography>
          </div>
        </Box>
      </DialogTitle>

      <DialogContent className="forecast-modal-content">
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <Typography>Loading tomorrow's forecasts...</Typography>
          </Box>
        ) : (
          <>
            {/* Summary Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={3}>
                <Box className="forecast-summary-card" sx={{ bgcolor: '#f3f4f6', p: 2, borderRadius: 2 }}>
                  <Typography variant="body2" color="textSecondary">Current Forecast</Typography>
                  <Typography variant="h5" fontWeight="bold">{formatCurrency(getTotalCurrent())}</Typography>
                  <Typography variant="caption" color="textSecondary">
                    {getTotalCurrentAppointments()} clients
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box className="forecast-summary-card" sx={{ bgcolor: '#e0f2fe', p: 2, borderRadius: 2 }}>
                  <Typography variant="body2" color="textSecondary">Revised Forecast</Typography>
                  <Typography variant="h5" fontWeight="bold" color="primary">{formatCurrency(getTotalRevised())}</Typography>
                  <Typography variant="caption" color="textSecondary">
                    {getTotalRevisedAppointments()} clients
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box className="forecast-summary-card" sx={{ 
                  bgcolor: totalChanges.revenue >= 0 ? '#dcfce7' : '#fee2e2', 
                  p: 2, 
                  borderRadius: 2 
                }}>
                  <Typography variant="body2" color="textSecondary">Revenue Change</Typography>
                  <Typography 
                    variant="h5" 
                    fontWeight="bold"
                    color={totalChanges.revenue >= 0 ? 'success.main' : 'error.main'}
                  >
                    {totalChanges.revenue >= 0 ? '+' : ''}{formatCurrency(totalChanges.revenue)}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Net change
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box className="forecast-summary-card" sx={{ 
                  bgcolor: totalChanges.appointments >= 0 ? '#ddd6fe' : '#fecaca', 
                  p: 2, 
                  borderRadius: 2 
                }}>
                  <Typography variant="body2" color="textSecondary">Client Change</Typography>
                  <Typography 
                    variant="h5" 
                    fontWeight="bold"
                    color={totalChanges.appointments >= 0 ? 'secondary.main' : 'error.main'}
                  >
                    {totalChanges.appointments >= 0 ? '+' : ''}{totalChanges.appointments}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Appointments
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {/* Revision Reason */}
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Reason for Revision</InputLabel>
              <Select
                value={revisionReason}
                onChange={(e) => setRevisionReason(e.target.value)}
                label="Reason for Revision"
              >
                {revisionReasons.map(reason => (
                  <MenuItem key={reason} value={reason}>{reason}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Staff Forecasts */}
            <Typography variant="h6" sx={{ mb: 2 }}>Staff Forecast Adjustments</Typography>
            <Grid container spacing={2}>
              {currentForecasts.map((forecast) => {
                const currentAmount = forecast.latest_forecast || forecast.original_forecast;
                const currentAppts = forecast.latest_appointments || forecast.original_appointments || 0;
                const revisedAmount = revisedForecasts[forecast.staff_member_id] || currentAmount;
                const revisedAppts = revisedAppointments[forecast.staff_member_id] || currentAppts;
                const revenueChange = revisedAmount - currentAmount;
                const apptsChange = revisedAppts - currentAppts;
                
                return (
                  <Grid item xs={12} md={6} key={forecast.staff_member_id}>
                    <Box 
                      className="forecast-staff-card" 
                      sx={{ 
                        p: 2, 
                        border: '1px solid #e5e7eb', 
                        borderRadius: 2,
                        bgcolor: 'white'
                      }}
                    >
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {forecast.staff_name}
                        </Typography>
                        <Box display="flex" gap={1}>
                          {revenueChange !== 0 && (
                            <Chip
                              label={`${revenueChange >= 0 ? '+' : ''}${formatCurrency(revenueChange)}`}
                              color={revenueChange >= 0 ? 'success' : 'error'}
                              size="small"
                            />
                          )}
                          {apptsChange !== 0 && (
                            <Chip
                              icon={<People />}
                              label={`${apptsChange >= 0 ? '+' : ''}${apptsChange}`}
                              color={apptsChange >= 0 ? 'primary' : 'warning'}
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      </Box>
                      
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={7}>
                          <Typography variant="caption" color="textSecondary" display="block">
                            Current: {formatCurrency(currentAmount)}
                          </Typography>
                          <TextField
                            type="number"
                            label="Revised Revenue"
                            value={revisedAmount}
                            onChange={(e) => handleForecastChange(forecast.staff_member_id, e.target.value)}
                            fullWidth
                            size="small"
                            InputProps={{
                              startAdornment: <Typography sx={{ mr: 1 }}>£</Typography>
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={5}>
                          <Typography variant="caption" color="textSecondary" display="block">
                            Current: {currentAppts} clients
                          </Typography>
                          <TextField
                            type="number"
                            label="Clients"
                            value={revisedAppts}
                            onChange={(e) => handleAppointmentChange(forecast.staff_member_id, e.target.value)}
                            fullWidth
                            size="small"
                            InputProps={{
                              startAdornment: <People fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>

            {/* Revision History */}
            {forecastHistory.length > 0 && (
              <>
                <Divider sx={{ my: 3 }} />
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <History />
                  <Typography variant="h6">Recent Revisions</Typography>
                </Box>
                <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                  {forecastHistory.map((revision, index) => (
                    <Box 
                      key={index} 
                      className="forecast-revision-item"
                      sx={{ 
                        p: 2, 
                        mb: 1, 
                        bgcolor: '#f9fafb', 
                        borderRadius: 1,
                        borderLeft: '3px solid #3b82f6'
                      }}
                    >
                      <Typography variant="body2">
                        <strong>{revision.staff_name}</strong>: {formatCurrency(revision.previous_forecast)} → {formatCurrency(revision.revised_forecast)}
                        {revision.revised_appointments && ` (${revision.revised_appointments} clients)`}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {revision.revision_reason} • {new Date(revision.revision_date).toLocaleString()}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions className="forecast-modal-actions" sx={{ p: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading || !revisionReason.trim()}
          startIcon={<Analytics />}
        >
          {loading ? 'Saving...' : 'Save Forecast Revisions'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DailyForecastUpdate;